import {
  Channels,
  Channel,
  SortOption,
  Choice,
  KeyValuePair,
  PACKAGE_INDICATORS
} from "@directv-digital/dds.services.channel-lineup-provider";
import { mockLocalData, mockRsnData } from "../utils/fallbackData";
import { deepClone } from "../utils/utilityFunctions";
import { filterCategoricalChannels, orderCategoriesExact, queryChannels } from "./filterMethods";
import { applyEntertainmentCategoryFallback, bucketChannelsByCategory, isChannelInAnyCategory } from "lib/helper/channels-service-helpers";
import { IPackage } from "../chnlListingPage.props";
import { filterAllChannelsNationalByDma, filterChannelsExcludedFromDisplayedPackages, handleNationalLocalDmaFilterChlup } from "lib/service/channels/get-local-national-dma-eligible-channels";

export interface IChannelsUI {
  bannerChannels: {
    [key: string]: Channel[] | any;
  };
  cmprGridChannelsAZ?: {
    ["INCLUDED_CHANNELS"]?: any[]; // Channel[] | any[];
    ["ADD_ON_CHANNELS"]?: any[] // Channel[] | any[];
    ["MYFREE_DTV"]?: any[];
  };
  cmprGridChannelsNum?: {
    ["INCLUDED_CHANNELS"]?: any[]; // Channel[] | any[];
    ["ADD_ON_CHANNELS"]?: any[] // Channel[] | any[];
    ["MYFREE_DTV"]?: any[];
  };
  cmprGridChannelsCategorical?: {
    [key: string]: Channel[] | any;
  }
}

export interface ChlupState {
  activeView: 'categories' | 'packages';
  cmprGridViewMode: SortOption;
  channels: any[];
  embeddedSvods: any[];
  searchChoicesSvods: Choice[];
  bannerChannels?: IChannelsUI["bannerChannels"];
  cmprGridChannelsAZ?: IChannelsUI["cmprGridChannelsAZ"];
  initialCmprGridChannelsAZ?: IChannelsUI["cmprGridChannelsAZ"]; // used to keep original data for filtering when in packages view
  cmprGridChannelsNum?: IChannelsUI["cmprGridChannelsNum"];
  initialCmprGridChannelsNum?: IChannelsUI["cmprGridChannelsNum"]; // used to keep original numeric data for filtering when in packages view
  cmprGridChannelsCategorical?: IChannelsUI["cmprGridChannelsCategorical"];
  initialCmprGridChannelsCategorical?: IChannelsUI["cmprGridChannelsCategorical"]; // used to keep original categorical data for filtering when in packages view
  searchChoices?: Choice[];
  needsUpdate: {
    banner: boolean;
    grid: boolean;
  },
  myFreeKey?: string;
  cache: {
    localChnls: any[];
    rsnChnls: any[];
    bannerCategories: KeyValuePair[];
    bannerPackages: IPackage[];
    gridCategoriesAZ: KeyValuePair[];
    gridCategories: KeyValuePair[];
    gridPackages: IPackage[];
    myFreeKey?: string;
  }
  currentDma: string;
  genreNationalMapping?: any[];
  genreMapping?: Record<string, string>;
  signatureExcludedLocalChannels?: any[];
}

// type ChannelTree = {
//   [category: string]: any[] | ChannelTree;
// }
// export interface IChannelsUI {
//   bannerChannels: ChannelTree;
//   cmprGridChannelsAZ?: ChannelTree;
//   cmprGridChannelsCategorical?: ChannelTree;
// }

export type ChlupStateActions =
  | { type: 'SET_LOADING_LOCAL_RSN_CHANNELS' }
  | { type: 'ADD_LOCAL_RSN_CHANNELS', data: { local: any[], rsn: any[], currentDma: string, genreNationalMapping?: any[], signatureExcludedLocalChannels?: any[] } }
  | { type: 'UPDATE_COMPONENT_VIEW',
      data: {
        selectedToggle?: string,
        // [WAT-11911] The tab's position in the tray — the non-editable half of the
        // view resolution, used when the display label is not one we recognise.
        selectedToggleIndex?: number
      }
    }
  | { type: 'REFRESH_BANNER' }
  | { type: 'REFRESH_PKGS_GRID' }

/**
 * [WAT-11771] A search choice, plus the two fields this app stamps onto it.
 *
 * `matchId` is the IDENTITY the search match is keyed on — see
 * filterMethods.ts's channelMatchesSearchChoice for why it, and not the channel number,
 * is the only field that identifies the row the user picked. `chnlNum` is retained
 * because the name fallback (for choices that carry no uuid, i.e. subscriptions) still
 * uses it to keep two same-named channels apart, and because FilterChips/DropdownList
 * surface it.
 *
 * 🔴 Be honest about what naming these here buys: DOCUMENTATION and editor completion,
 * NOT an enforced contract. `Choice` (from the channel-lineup-provider package) is
 * declared with a blanket index signature, so TypeScript accepts BOTH the write in
 * getSearchChoices and the read in channelMatchesSearchChoice — and would equally accept
 * a misspelt `matchID`, or the field being dropped entirely, with no error at either end.
 * Both stay optional because a subscription choice genuinely has neither. The real guard
 * is the regression test in filterMethods.spec.ts (and searchSeam.spec.ts) which fails if the
 * matchId stops being compared and the match silently degrades to name-only.
 */
export type SearchChoice = Choice & {
  /** Feed identity — see getSearchChoices. NOT the display `uuid`, which grid builders rewrite. */
  matchId?: string;
  chnlNum?: string;
  offerCode?: string;
};

export interface ChannelFilter {
  product?: 'stream' | 'satellite';
  searchList?: SearchChoice[];
  categories?: string[];
  chnlPackages?: string[];
  addOnPromoAny?: boolean;
  includedAny?: boolean;
  local?: boolean;
  rsn?: boolean;
  myFreeDtv?: boolean;
  genrePacks?: string[] | 'all';
};

/** ----------------------------------------------------------------------------
 * Helper Functions
 */

// `Partial<...>`, not a bare `Record<...>`: the lookup below misses for any label we do not
// know, so the value type says out loud that `undefined` is a real outcome. Nothing in this
// project's tsconfig makes that binding (`strictNullChecks` is off) — the `if (knownView)`
// guard is what actually handles the miss; the annotation is there so the next reader of this
// table does not read it as a promise that every string resolves to a view.
const TOGGLE_LABEL_VIEWS: Partial<Record<string, ChlupState['activeView']>> = {
  'SIGNATURE PACKAGES': 'packages',
  PACKAGES: 'packages',
  CATEGORY: 'categories',
  'GENRE PACKS': 'categories',
  'ALL CHANNELS': 'categories'
};

/**
 * [WAT-11911] Resolve the view a tab shows. The CMS `label` is an EDITABLE display
 * string, so it cannot be the only thing that decides this: renaming the first tab
 * ("Signature Packages" -> "Streaming Packages", prod 2026-09-02) used to return
 * undefined here, which built every grid empty and left the page blank with nothing
 * logged.
 *
 * Known labels still map straight through. Anything else falls back to the tab's
 * POSITION, which an author cannot change by renaming: tab 0 is the signature /
 * packages grid, any later tab is the genre / categories view — the same contract
 * ChnlListingPage already relies on when it seeds toggles[0]/toggles[1]. A tray that
 * both renames a tab AND reorders it away from that contract still degrades to a
 * rendered page rather than a blank one, and the warning names the label that did it.
 *
 * Two deliberate calls on the position argument:
 * - A MISSING position (a caller with no tray to read one from) means tab 0, the packages
 *   default the page has always used. Note this is NOT the satellite path: satellite does
 *   pass a position, and its hardcoded "PACKAGES" is a known label, so it returns above.
 * - A NEGATIVE position is a `findIndex` miss, i.e. we could not identify the tab. That
 *   is not evidence of a later tab, so it also degrades to the first-tab default.
 * An ABSENT label is not an authoring mistake — a tray can legitimately have none — so
 * it takes the same fallback WITHOUT the warning. Only a label someone actually typed,
 * and we do not recognise, is worth telling them about.
 */
export function getComponentView(selectedToggle?: string, toggleIndex?: number): ChlupState['activeView'] {
  const knownView = TOGGLE_LABEL_VIEWS[selectedToggle?.toUpperCase()];
  if (knownView) {
    return knownView;
  }
  const fallbackView = (toggleIndex ?? 0) > 0 ? 'categories' : 'packages';
  if (selectedToggle) {
    // The operational half of this warning is deliberately NOT env-gated, unlike the
    // genreMapping collision warn further down this file: prod 2026-09-02 blanked this page
    // with nothing in any log, and that silence is the defect. What the prod branch drops is
    // only the AUTHORING hint — the CMS field name and which story to open. That is a small
    // withholding, not a redaction: the label, the position and the chosen view all still
    // print, because those are what make the signal actionable for on-call.
    const message = `[ChlupStore] Unrecognised tab label ${JSON.stringify(selectedToggle)} at tab position ${toggleIndex ?? 0} — rendering the "${fallbackView}" view.`;
    console.warn(
      process.env.NODE_ENV === 'production' ?
        message :
        `${message} Check the tabTrayBtn toggle labels on the channel-lineup story in Storyblok.`
    );
  }
  return fallbackView;
};

/** ***** Banner --------------------------------------------------------------- */
/**
 * Create display-ready map of channels for the banner view
 * - See IChannelsUI["bannerChannels"] for format
 * - Keep as optimized as possible
 */
function getBannerChannels(
  channels: any[],
  categories: KeyValuePair[], // either genreAndMiniCategories or sigBannerCategories
  // packages: IPackage[], // either genrePackages or signaturePackages
  myFreeKey: string,
  currentDma: string,
  genreNationalMapping?: any[],
  genreMapping?: Record<string, string>
): IChannelsUI["bannerChannels"] {
  try {
    const chnls: IChannelsUI["bannerChannels"] = {};
    if(myFreeKey) {
      chnls[myFreeKey] = [];
    }
    for (const channel of channels) {
      for (const pkg of channel?.chnlPackages ?? []) {
        const keyMatch = (pkg?.match(/ATT_TV_(.*)/)?.[1] || "").toUpperCase();

        if (categories?.some(ctg => ctg?.key === keyMatch)) {
          const ctgKey = categories?.find(ctg => ctg?.key === pkg?.match(/ATT_TV_(.*)/)?.[1])?.key;

          // console.debug("[ChlupStore] adding", channel?.name, " to ", ctgKey);
          if (!chnls[ctgKey]) {
            chnls[ctgKey] = [channel] as any[];
          } else {
            chnls[ctgKey].push(channel);
          }
        }
      }
      // add MyFree Channels into MyFree section
      const isMyFreeDTV = channel?.isMyFreeDTV;
      if (isMyFreeDTV && myFreeKey) {
        chnls[myFreeKey].push(channel);
      }
    }
    // Genre Local/National DMA Eligibility check - start
    const shortNameToQueryParam: Record<string, string> = {};
    if (genreMapping) {
      for (const [qp, shortName] of Object.entries(genreMapping)) {
        shortNameToQueryParam[shortName?.toUpperCase()] = qp;
        const withoutPrefix = shortName?.toUpperCase()?.replace(/^ATT_TV_/, '');
        if (withoutPrefix) {
          if (process.env.NODE_ENV !== 'production' && shortNameToQueryParam[withoutPrefix] && shortNameToQueryParam[withoutPrefix] !== qp) {
            console.warn(`[ChlupStore] genreMapping key collision on "${withoutPrefix}": overwriting "${shortNameToQueryParam[withoutPrefix]}" with "${qp}"`);
          }
          shortNameToQueryParam[withoutPrefix] = qp;
        }
      }
    }
    // eslint-disable-next-line guard-for-in
    for (const ctg in chnls) {
      try {
        const genrePackChannels = [...(chnls[ctg] || [])];
        const packageKey = shortNameToQueryParam[ctg?.toUpperCase()] || ctg;
        const filteredChannels = handleNationalLocalDmaFilterChlup({
          nationalChannels: genrePackChannels,
          currentDma,
          nationalMapping: genreNationalMapping,
          packageKey,
          categoryKey: ctg
        });
        chnls[ctg] = Array.isArray(filteredChannels) ? filteredChannels : genrePackChannels;
      } catch (err) {
        console.error(`[ChlupStore] Error while handleNationalLocalDmaFilterChlup for category ${ctg}:`, err);
        chnls[ctg] = chnls[ctg] || [];
      }
    }
    // Genre Local/National DMA Eligibility check - end
    console.debug(`[ChlupStore] getBannerChannels finished`, chnls)
    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error in getBannerChannels", e);
    return {};
  }
};

const edspSortKeyArray = [
  "SPORTS", "RSN", "LOCAL",
  "ENTERTAINMENT", "FAMILY_AND_KIDS", "MOVIES_AND_EVENT", "NEWS_AND_INFORMATION", "PREMIUM_TV",
  "ADD_ON_CHANNELS"
];

export function getDisplayedEdspBannerCategories(
  selectedCategories: KeyValuePair[],
  allCategoriesKV: KeyValuePair[],
  isLoadingLocalRsn: boolean
) {
  const displayCategories: KeyValuePair[] = [];

  if (selectedCategories?.length === 0) {
    return allCategoriesKV;
  }
  const selectedCategoryKeys = selectedCategories?.map(ctg => ctg?.key);

  for (const ctg of allCategoriesKV) {
    if (ctg?.key === "ADD_ON_CHANNELS") {
      displayCategories.push(ctg);
    }
    if (selectedCategoryKeys?.includes(ctg.key)) {
      displayCategories.push(ctg);
    }
  }

  if (isLoadingLocalRsn) {
    displayCategories.push({
      key: "LOCAL",
      value: "Local Channels"
    });
    displayCategories.push({
      key: "RSN",
      value: "Regional Sports"
    });
  }

  return orderCategoriesExact(displayCategories, edspSortKeyArray);
};

/** ***** Package Comparison Grid----------------------------------------------- */
/**
 * Create display-ready map of channels for the Packages tab ALPHABETICAL View
 * - See IChannelsUI["bannerChannels"] for format
 */
function getCmprGridChannelsAZ(
  channels: any[],
  categories: KeyValuePair[],
  packages: IPackage[],
  myFreeKey: string
) {
  try {
    const chnls: IChannelsUI["cmprGridChannelsAZ"] = {};
    const indicatorIxs = packages.reduce((pkgs, pkg) => {
      pkgs.push(pkg?.packageIndicatorIndex);
      return pkgs;
    }, []);

    for (const channel of channels) {
      if (channel.category === undefined || channel.category === "") {
        console.warn("[ChlupStore] getCmprGridChannelsAZ: channel not added, category is undefined or empty", channel?.name);
        continue;
      }

    const chnlCategory = channel.category?.toUpperCase();
    // Structural LOCAL/RSN membership — detect via the flags set on the built
    // lineup channel (buildLocalRsnChannel); the case-normalized `category` scalar
    // is kept as a Stage-1 safety net (every built LOCAL/RSN channel carries both).
    const isLocalRsn = Boolean(channel?.isLocal || channel?.isRsn) || chnlCategory === "LOCAL" || chnlCategory === "RSN";
    const isInCategory = isChannelInAnyCategory(channel, categories as any);
    const isIncAny = // isIncAny === ENGLISH
      isLocalRsn || channel?.isInclAny
    const isAddOnPromoAny =
      !isLocalRsn && channel?.isAddOnPromoAny;
    const isMyFreeDTV = channel?.isMyFreeDTV;
    // const c = new Channel(channel);

      if (isIncAny && isInCategory && !isMyFreeDTV && !channel?.signatureExcluded) {
        const c = {
          ...channel,
          type: "Included",
          uuid: `Included-${channel?.uuid}`,
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
            ?.map(ind => (ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null))
        }
        if (!chnls?.INCLUDED_CHANNELS) {
          chnls.INCLUDED_CHANNELS = [c] as any[];
        } else {
          chnls.INCLUDED_CHANNELS.push(c);
        }
      }
      if (isAddOnPromoAny && isInCategory && !isMyFreeDTV) {
        const c = {
          ...channel,
          uuid: `AddOn-${channel?.name}-${channel?.chnlNum}`,
          type: "AddOn",
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
        }
        if (!chnls?.ADD_ON_CHANNELS) {
          chnls.ADD_ON_CHANNELS = [c];
        } else {
          chnls.ADD_ON_CHANNELS.push(c);
        }
      }
      if (isMyFreeDTV && myFreeKey) {
        const c = {
          ...channel,
          uuid: `MyFreeDTV-${channel?.name}-${channel?.chnlNum}`,
          type: "MyFreeDTV",
          indicators: indicatorIxs?.map(_ix => 1)
        };
        if (!chnls?.[myFreeKey]) {
          chnls[myFreeKey] = [c];
        } else {
          chnls[myFreeKey].push(c);
        }
      }
    }
    console.debug(`[ChlupStore] getCmprGridChannelsAZ finished`, chnls)
    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error: in getCmprGridChannelsAZ", e);
    return {};
  }
}
/**
 *
 * @param channels
 * @param categories
 * @param packages
 * @returns channels in a format ready for the Packages tab NUMERIC View
 * Create display-ready map of channels for the Packages tab NUMERIC View
 * - See IChannelsUI["cmprGridChannelsNum"] for format
 * - Collects included and add-on channels separately
 * - Sorts both arrays by channel number
 * - Uses packageIndicatorIndex to filter indicators
 * - Returns an object with two arrays: INCLUDED_CHANNELS and ADD_ON_CHANNELS
 */
function getCmprGridChannelsNum(
  channels: any[],
  categories: KeyValuePair[],
  packages: IPackage[],
  myFreeKey: string
) {
  try {
    const chnls: IChannelsUI["cmprGridChannelsNum"] = {};
    const indicatorIxs = packages.reduce((pkgs, pkg) => {
      pkgs.push(pkg?.packageIndicatorIndex);
      return pkgs;
    }, []);

    // Collect included and add-on channels
    const includedChannels: any[] = [];
    const addOnChannels: any[] = [];
    const myFreeDtvChannels: any[] = [];

    for (const channel of channels) {
      if (channel.category === undefined || channel.category === "") {
        console.warn("[ChlupStore] getCmprGridChannelsNum: channel not added, category is undefined or empty", channel?.name);
        continue;
      }

      const chnlCategory = channel.category?.toUpperCase();
      // Structural LOCAL/RSN membership — flags first, scalar `category` as Stage-1 fallback.
      const isLocalRsn = Boolean(channel?.isLocal || channel?.isRsn) || chnlCategory === "LOCAL" || chnlCategory === "RSN";
      const isInCategory = isChannelInAnyCategory(channel, categories as any);
      const isIncAny =
        isLocalRsn || channel?.isInclAny;
      const isAddOnPromoAny =
        !isLocalRsn && channel?.isAddOnPromoAny;
      const isMyFreeDTV = channel?.isMyFreeDTV;

      if (isIncAny && isInCategory && !isMyFreeDTV && !channel?.signatureExcluded) {
        const c = {
          ...channel,
          type: "Included",
          uuid: `Included-${channel?.uuid}`,
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
            ?.map(ind => (ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null))
        };
        includedChannels.push(c);
      }
      if (isAddOnPromoAny && isInCategory && !isMyFreeDTV) {
        const c = {
          ...channel,
          uuid: `AddOn-${channel?.name}-${channel?.chnlNum}`,
          type: "AddOn",
          indicators: channel?.indicators
            ?.filter((_, ix) => indicatorIxs?.includes(ix))
        };
        addOnChannels.push(c);
      }
      if (isMyFreeDTV) {
        const c = {
          ...channel,
          uuid: `MyFreeDTV-${channel?.name}-${channel?.chnlNum}`,
          type: "MyFreeDTV",
          indicators: indicatorIxs?.map(_ix => 1)
        };
        myFreeDtvChannels.push(c);
      }
    }
    // Sort both arrays by channel number
    includedChannels.sort((a, b) => Number(a.chnlNum) - Number(b.chnlNum));
    addOnChannels.sort((a, b) => Number(a.chnlNum) - Number(b.chnlNum));
    myFreeDtvChannels.sort((a, b) => Number(a.chnlNum) - Number(b.chnlNum));
    if (includedChannels.length > 0) {
      chnls.INCLUDED_CHANNELS = includedChannels;
    }
    if (addOnChannels.length > 0) {
      chnls.ADD_ON_CHANNELS = addOnChannels;
    }
    if (myFreeDtvChannels.length > 0 && myFreeKey) {
      chnls[myFreeKey] = myFreeDtvChannels;
    }

    console.debug(`[ChlupStore] getCmprGridChannelsNum finished`, chnls);
    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error: in getCmprGridChannelsNum", e);
    return {};
  }
}

/**
 * Create display-ready map of channels for the Packages tab CATEGORICAL View
 * - See IChannelsUI["bannerChannels"] for format
 */
function getCmprGridChannelsCategorical(
  channels: any[],
  categories: KeyValuePair[],
  packages: IPackage[],
  myFreeKey: string
) {
  try {
    const chnls: IChannelsUI["cmprGridChannelsCategorical"] = {};
    const indicatorIxs = packages.reduce((pkgs, pkg) => {
      pkgs.push(pkg?.packageIndicatorIndex);
      return pkgs;
    }, []);

  // Genre accordions: one call, CMS-driven. Each channel appears under every
  // label whose engCategories it matches. No key translation, no hardcoding.
  const genreBuckets = bucketChannelsByCategory(channels, categories as any);
  for (const [label, labelChannels] of Object.entries(genreBuckets)) {
    for (const channel of labelChannels) {
      // Structural LOCAL/RSN membership — flags first, scalar `category` as Stage-1 fallback.
      const isLocalRsn = Boolean(channel?.isLocal || channel?.isRsn) || ["LOCAL","RSN"].includes((channel?.category||"").toUpperCase());
      const isIncAny = channel?.isInclAny || isLocalRsn;
      // AddOn gate matches the A-Z / Channel-# views (and pre-Phase-G prod): a
      // non-local channel that is both Included AND an add-on shows in both
      // sub-sections. Phase G's categorical rewrite incidentally flipped this to
      // !isIncAny; restored here for cross-view + prod parity.
      const isAddOnPromoAny = !isLocalRsn && channel?.isAddOnPromoAny;
      if (channel?.isMyFreeDTV) continue; // handled below
      if (isIncAny && !channel?.signatureExcluded) {
        const c = {
          ...channel,
          uuid: `Included-${channel?.name}-${channel?.chnlNum}-${label}`,
          type: "Included",
          indicators: channel?.indicators
            ?.filter((_: any, ix: number) => indicatorIxs?.includes(ix))
            ?.map((ind: any) => ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null)
        };
        if (!chnls[label]) chnls[label] = [] as any[];
        (chnls[label] as any[]).push(c);
      }
      if (isAddOnPromoAny) {
        const c = {
          ...channel,
          uuid: `AddOn-${channel?.name}-${channel?.chnlNum}-${label}`,
          type: "AddOn",
          indicators: channel?.indicators?.filter((_: any, ix: number) => indicatorIxs?.includes(ix))
        };
        if (!chnls["ADD_ON_CHANNELS"]) chnls["ADD_ON_CHANNELS"] = {};
        if (!(chnls["ADD_ON_CHANNELS"] as any)[label]) (chnls["ADD_ON_CHANNELS"] as any)[label] = [] as any[];
        (chnls["ADD_ON_CHANNELS"] as any)[label].push(c);
      }
    }
  }

  // Structural channels: RSN (Regional Sports) and Local — injected by the
  // zip/DMA API. Membership is detected via the structural flags (set on the built
  // channel); the scalar `category` is a Stage-1 fallback. The resolved key stays
  // "RSN"/"LOCAL" because that IS the canonical structural accordion section label.
  for (const channel of channels) {
    const structCat = channel?.isRsn
      ? "RSN"
      : channel?.isLocal
        ? "LOCAL"
        : (channel?.category || "").toUpperCase();
    if (structCat !== "RSN" && structCat !== "LOCAL") continue;
    if (channel?.isMyFreeDTV) continue;
    const c = {
      ...channel,
      uuid: `Structural-${channel?.name}-${channel?.chnlNum}-${structCat}`,
      type: "Included",
      indicators: channel?.indicators
        ?.filter((_: any, ix: number) => indicatorIxs?.includes(ix))
        ?.map((ind: any) => ind === PACKAGE_INDICATORS.IN ? PACKAGE_INDICATORS.IN : null)
    };
    if (!chnls[structCat]) chnls[structCat] = [] as any[];
    (chnls[structCat] as any[]).push(c);
  }

  // MyFree channels
  for (const channel of channels) {
    const isMyFreeDTV = channel?.isMyFreeDTV;
    if (isMyFreeDTV && myFreeKey) {
      const c = {
        ...channel,
        uuid: `MyFreeDTV-${channel?.name}-${channel?.chnlNum}`,
        type: "MyFreeDTV",
        indicators: indicatorIxs?.map((ix: any) => 1)
      };
      if (!chnls?.[myFreeKey])
        chnls[myFreeKey] = [c];
      else
        chnls[myFreeKey].push(c);
    }
  }

    return chnls;
  } catch (e) {
    console.error("[ChlupStore] Error: in getCmprGridChannelsCategorical", e);
    return {};
  }
}

/** ***** Search bar ---------------------------------------------------------- */
export function getSearchChoices(channels: any[], type?: string): SearchChoice[] {
  const choices = [];
  // Guard the input: the filtered channel source can be undefined on its own error path,
  // and this fn (unlike the banner/grid builders) has no try/catch to absorb a bad iterable. [WAT-11301]
  for (const c of channels || []) {
    if (!c) {
      continue;
    }
    choices.push({
      displayText: c.name,
      category: c.category,
      chnlPackages: c.chnlPackages,
      value: c.name,
      // [WAT-11771] The identity the grid filter matches on. Minted once per channel
      // (channel-lineup-parser.ts server-side, buildLocalRsnChannel for local/RSN).
      //
      // 🔴 `matchId`, NOT `uuid`. They hold the same value here, but `uuid` is the DISPLAY
      // key and the grid builders below OVERWRITE it per bucket (`Included-${uuid}`,
      // `AddOn-${name}-${chnlNum}`, …) so React keys stay unique when one channel appears
      // in several buckets. Matching on `uuid` therefore compared "Shudder TV-557-185"
      // against "Included-Shudder TV-557-185" — never equal — and every Packages-tab search
      // came back EMPTY. `matchId` survives because the builders spread the row and rewrite
      // only `uuid`.
      //
      // Absent on a subscription choice (`c` is an EmbeddedSvods entry), which falls back
      // to the name match.
      matchId: c.matchId,
      // [WAT-11717] Channel display names are not guaranteed unique (see
      // utilityFunctions.ts). Kept alongside `uuid` because the name fallback uses it to
      // keep two same-named channels apart, and because the chips/dropdown surface it.
      // NOT the primary match key — a channel number is a tuner position that distinct
      // channels share (WAT-11771). Undefined on an SVOD choice.
      // [WAT-11774] Coerced with String(), not a template literal — the template is exactly
      // what produced the truthy "undefined" sentinel in buildLocalRsnChannel. Raw local/RSN
      // rows carry NUMERIC channel numbers (this repo's own fixture uses `chnlNum: 7`), so
      // the declared `string` type only held because the consumer defensively re-coerced.
      chnlNum: c?.chnlNum === null || c?.chnlNum === undefined ? undefined : String(c.chnlNum),
      // [WAT-11772] The stable identity for a SUBSCRIPTION choice, the way `uuid` is for a
      // channel. Undefined on a channel choice. Present on a subscription because
      // embeddedSVODParserChlup now carries it through.
      offerCode: c.offerCode,
      type
    });
  }
  return choices;
}

/**
 * [WAT-11772] Normalized subscription label, for the paths where the code can't decide alone.
 */
export const normalizeSvodLabel = (name?: string): string => {
  if (name === null || name === undefined) {
    return "";
  }
  return String(name).trim().toLowerCase();
};

/**
 * [WAT-11772] Dedupe key for a SUBSCRIPTION choice.
 *
 * 🔴 An offer code is an OFFER identity, not a ROW identity. A split bundle authors SEVERAL
 * rendered entries under ONE code — the live shape is Disney+ and Hulu both on
 * `BOLT-DUOBASIC-202411`, which is exactly why the adapter carries an `entriesPerCode` gate.
 * Keying such a code on the code alone made the second half collide with the first and get
 * dropped from the choice list: its logo renders on the card while typing its name offers
 * nothing — the very defect this ticket exists to close, reintroduced through the new source.
 *
 * So the code decides ALONE only when it identifies one label. When a code is shared, the
 * normalized label qualifies it, keeping the halves distinct. `sharedCodes` is computed once
 * per build across both sources, so a code that is shared anywhere is treated as shared
 * everywhere — the sharing is a property of the authoring, not of a particular pack.
 */
const svodChoiceKey = (offerCode?: string, name?: string, sharedCodes?: Set<string>): string => {
  const code = offerCode === null || offerCode === undefined ? "" : String(offerCode).trim();
  const label = normalizeSvodLabel(name);
  if (code) {
    return sharedCodes?.has(code) ? `code:${code}|${label}` : `code:${code}`;
  }
  return label ? `name:${label}` : "";
};

/**
 * [WAT-11772] Offer codes that a SINGLE list renders under more than one label — the split
 * bundles.
 *
 * 🔴 Per-list, not global, and the distinction is the whole point:
 *   • A SPLIT BUNDLE puts two labels under one code in the SAME list — Disney+ and Hulu both
 *     on `BOLT-DUOBASIC-202411`, rendered side by side on one card. Two different products
 *     the customer can search for separately, so they must stay two choices.
 *   • LABEL DIVERGENCE puts one label in each list — page-level "Disney+" vs the MyKids pack's
 *     "Disney+ Basic", one product named differently by two sources. That is the case offer-code
 *     matching exists for, and it must still collapse to ONE choice (AC6).
 * Counting labels globally cannot tell these apart — it sees two labels either way and would
 * re-split the divergence case into two choices that look unrelated to the customer.
 */
const findSharedSvodCodes = (pageSvods: any[], bannerPackages?: any[]): Set<string> => {
  const shared = new Set<string>();
  const scanList = (list: any[]) => {
    const labelsByCode: { [code: string]: Set<string> } = {};
    (list || []).forEach((svod: any) => {
      const code = svod?.offerCode === null || svod?.offerCode === undefined ? "" : String(svod.offerCode).trim();
      const label = normalizeSvodLabel(svod?.name);
      if (!code || !label) {
        return;
      }
      labelsByCode[code] = labelsByCode[code] || new Set<string>();
      labelsByCode[code].add(label);
    });
    Object.keys(labelsByCode).forEach(code => {
      if (labelsByCode[code].size > 1) {
        shared.add(code);
      }
    });
  };
  scanList(pageSvods);
  (bannerPackages || []).forEach(pkg => scanList(pkg?.svods));
  return shared;
};

/**
 * [WAT-11772] Every subscription the customer can search for, from BOTH places this page
 * keeps them.
 *
 * The search box used to be handed only the PAGE-LEVEL `embeddedSvods` list. The genre packs
 * carry their own, separate subscriptions on `bannerPackages[].svods`, and a subscription
 * that lives ONLY there could never become a chip — so it was unreachable no matter what the
 * customer typed. On the live stage feed that is AMC+ with Ads (MyEntertainment), ViX Premium
 * with ads (MiEspañol), Disney+ Basic (MyKids) and HBO Max Basic With Ads: four products
 * visible on the page in browse mode and absent from search. QA reported the two that exist
 * ONLY on a genre pack.
 *
 * Deduped by offer code so a product carried by both lists — under DIFFERENT labels, e.g.
 * page-level "Disney+" vs pack "Disney+ Basic" — is offered ONCE, not as two choices that
 * look unrelated. The page-level label wins because that list is the one the packages grid
 * also renders. Matching is by code downstream, so the surviving label reaches both surfaces.
 */
export function getSvodSearchChoices(pageSvods: any[], bannerPackages?: any[]): SearchChoice[] {
  const choices = getSearchChoices(pageSvods, "svod");
  const sharedCodes = findSharedSvodCodes(pageSvods, bannerPackages);
  const seen = new Set<string>();
  choices?.forEach(choice => {
    const key = svodChoiceKey(choice?.offerCode, choice?.value as string, sharedCodes);
    if (key) {
      seen.add(key);
    }
  });
  for (const pkg of bannerPackages || []) {
    for (const svod of pkg?.svods || []) {
      const key = svodChoiceKey(svod?.offerCode, svod?.name, sharedCodes);
      // Skip a blank-labelled entry outright rather than letting it claim a dedupe slot: it
      // would push an unsearchable empty chip AND block the properly-named entry for the
      // same code on another pack.
      if (!key || !normalizeSvodLabel(svod?.name) || seen.has(key)) {
        continue;
      }
      seen.add(key);
      choices.push({
        displayText: svod?.name,
        value: svod?.name,
        offerCode: svod?.offerCode,
        // A genre-pack subscription has no `chnlPackages`: it is not carried by a signature
        // tier, so it contributes no column to the packages grid. Empty, not undefined, so
        // consumers that map over it are unchanged.
        chnlPackages: [],
        type: "svod"
      });
    }
  }
  return choices;
}

/** ----------------------------------------------------------------------------
 * STATE INITIALIZATION
 * - Only data for the initial view is loaded immediately. Channel data for other
 *   pages is loaded idly thru the needsUpdate flag.
 */
export function initializeChlupStore(data): ChlupState {
  const initComponentView = getComponentView(data?.selectedToggle, data?.selectedToggleIndex);
  // Category fallback (one pass, all three signature views): includable channels
  // whose categories[] match no configured content accordion (e.g. format-only
  // "DIRECTV 4K Live") are dropped by the AZ/Channel#/Categorical gates. Bucket
  // them under Entertainment here, on the shared channel list + the SAME
  // gridCategories the builders match against, so it also covers the reducer's
  // lazy rebuilds (which re-derive from state.channels + cache.gridCategories).
  // Clone-on-write: the helper returns a NEW array (WAT-11076 — channel ENTRIES may be frozen
  // cache objects, so it never mutates them in place). We assign that array onto `data.channels`
  // so the builders + the reducer's lazy rebuilds below read the fallback-applied channels.
  // INVARIANT (re: input mutation): `data` here is the client-side, JSON-parsed /api/channel-data
  // envelope — a fresh, mutable object — NOT the server's deep-frozen cache object (that exists
  // only server-side; the cache is never handed to this client store). So this property write is
  // safe, and the repo's `no-param-reassign` is configured `props:false`, which permits it. If a
  // future caller ever passes a frozen `data` envelope, switch to a local copy + thread it through.
  if (data?.channels) {
    data.channels = applyEntertainmentCategoryFallback(data.channels, data?.gridCategories);
  }
  // Two tiers [WAT-11301]:
  //  • visible — the genre-nationals DMA gate ONLY. signatureExcludedLocalChannels is a
  //    SIGNATURE-tier concern and must NOT touch the genre (MiEspañol) view, so the banner +
  //    search are driven purely by genre eligibility (genreLocalChannelMapping / genre-nationals).
  //  • per-package (the displayed signature tiers) — feeds ONLY the signature grids, where
  //    signatureExcludedLocalChannels applies, so that national never renders a ✓ or "$" there.
  // Genre queryparams = the keys of the adapter's genreMapping (queryparam -> short name) — the
  // page's authoritative genre list, same config family the agent tool derives from. [WAT-11301]
  const initVisibleChannels = filterAllChannelsNationalByDma(
    data?.channels, data?.genreNationalMapping, data?.currentDma, Object.keys(data?.genreMapping || {})
  );
  const initSignatureGridChannels = filterChannelsExcludedFromDisplayedPackages(
    data?.channels, data?.currentDma, data?.signatureExcludedLocalChannels,
    (data?.gridPackages || []).map(p => p?.id).filter(Boolean), data?.genreNationalMapping
  );
  // Generated by Copilot- Start - 2026-03-13 13:38:18
  const initialCmprGridChannelsAZ = initComponentView === 'packages' ? getCmprGridChannelsAZ(
    initSignatureGridChannels,
    data?.gridCategories,
    data?.gridPackages,
    data?.myFreeKey
  ) : {};
  // Generated by Copilot- End - 2026-03-13 13:38:18
  // Generated by Copilot- Start - 2026-03-13 18:30:40
  const initialCmprGridChannelsNum = initComponentView === 'packages' ? getCmprGridChannelsNum(
    initSignatureGridChannels,
    data?.gridCategories,
    data?.gridPackages,
    data?.myFreeKey
  ) : {};
  // Generated by Copilot- End - 2026-03-13 18:30:40
  // Generated by Copilot- Start - 2026-03-13 19:27:37
  const initialCmprGridChannelsCategorical = initComponentView === 'packages' ? getCmprGridChannelsCategorical(
    initSignatureGridChannels,
    data?.gridCategories,
    data?.gridPackages,
    data?.myFreeKey
  ) : {};
  // Generated by Copilot- End - 2026-03-13 19:27:37

  const clupState: ChlupState = {
    channels: data?.channels,
    embeddedSvods: data?.embeddedSvods,
    // [WAT-11256 round-2 S5] Gate the SVOD CHIP source on the same flag every SVOD
    // ROW render path already respects (ChnlListingPage.tsx only unshifts the
    // INCLUDED_SUBSCRIPTIONS category `if (isEmbeddedSvodsEnabled)`). Without this,
    // flag-OFF-with-authored-SVODs — a reachable CMS state, since the adapter only
    // force-disables the flag when embeddedSvods is empty — still offers a selectable
    // SVOD chip that the grid then can't honor.
    // [WAT-11772] Both subscription sources, still behind the SAME flag every SVOD render
    // path respects — a genre-pack subscription is no more selectable than a page-level one
    // when the flag is off.
    searchChoicesSvods: data?.isEmbeddedSvodsEnabled ?
      getSvodSearchChoices(data?.embeddedSvods, data?.bannerPackages) : [],
    bannerChannels: initComponentView === 'categories' ? getBannerChannels(
      initVisibleChannels,
      data?.bannerCategories,
      // data?.bannerPackages,
      data?.myFreeKey,
      data?.currentDma,
      data?.genreNationalMapping,
      data?.genreMapping
    ) : {},
    cmprGridChannelsAZ: initialCmprGridChannelsAZ,
    initialCmprGridChannelsAZ: deepClone(initialCmprGridChannelsAZ), // used to keep original data for filtering when in packages view
    cmprGridChannelsNum: initialCmprGridChannelsNum,
    initialCmprGridChannelsNum: deepClone(initialCmprGridChannelsNum), // used to keep original numeric data for filtering when in packages view
    cmprGridChannelsCategorical: initialCmprGridChannelsCategorical,
    initialCmprGridChannelsCategorical: deepClone(initialCmprGridChannelsCategorical), // used to keep original categorical data for filtering when in packages view
    searchChoices: getSearchChoices(initVisibleChannels),
    cmprGridViewMode: data?.cmprGridViewMode,
    activeView: initComponentView,
    needsUpdate: {
      banner: true,
      grid: true
    },
    myFreeKey: data?.myFreeKey,
    cache: {
      localChnls: [],
      rsnChnls: [],
      bannerCategories: data?.bannerCategories,
      bannerPackages: data?.bannerPackages,
      gridCategoriesAZ: data?.gridCategoriesAZ,
      gridCategories: data?.gridCategories,
      gridPackages: data?.gridPackages,
      myFreeKey: data?.myFreeKey
    },
    currentDma: data?.currentDma,
    genreNationalMapping: data?.genreNationalMapping,
    genreMapping: data?.genreMapping,
    signatureExcludedLocalChannels: data?.signatureExcludedLocalChannels
  };

  return clupState;
}

// Generated by Copilot- Start
function removeChannelsByAliases(channelsData: Record<string, any>, channelAliases: string[]) {
  const channelsDataCopy = deepClone(channelsData);
  for (const category in channelsDataCopy) {
    if (Array.isArray(channelsDataCopy[category])) {
      channelsDataCopy[category] = channelsDataCopy[category].filter(channel => !channelAliases.includes(channel.name));
    } else if (typeof channelsDataCopy[category] === 'object' && channelsDataCopy[category] !== null) {
      for (const subCategory in channelsDataCopy[category]) {
        if (Array.isArray(channelsDataCopy[category][subCategory])) {
          channelsDataCopy[category][subCategory] = channelsDataCopy[category][subCategory].filter(channel => !channelAliases.includes(channel.name));
        }
      }
    }
  }

  return channelsDataCopy;
}

function getNationalAliasesForLocalChannels(localChannels, genreNationalMapping) {
  // Null-safe over both the arrays and their elements — a null mapping/local row must not throw
  // the reducer (this fn has no try/catch of its own). [WAT-11301]
  return (genreNationalMapping || [])
    .filter(genreNational => (localChannels || []).some(localChannel => localChannel?.name === genreNational?.channel))
    .flatMap(genreNational => genreNational?.channelAliases || []);
}
// Generated by Copilot- End

/**
 * Signature-grid source from current state: the per-package signatureExcluded filter over the
 * displayed tiers, plus the alias dedup against the locals already in the lineup (a master-feed
 * alias copy like "Telemundo East" is hidden when its base-named local is present). Used by every
 * signature-grid REBUILD (view toggle + REFRESH_PKGS_GRID), so a rebuild can never resurrect a
 * channel the ADD_LOCAL_RSN_CHANNELS bucket patch had stripped. [WAT-11301]
 */
function getSignatureGridSource(state: ChlupState) {
  const aliases = getNationalAliasesForLocalChannels(state.cache?.localChnls || [], state.genreNationalMapping || []);
  return filterChannelsExcludedFromDisplayedPackages(
    state?.channels, state.currentDma, state.signatureExcludedLocalChannels,
    (state?.cache?.gridPackages || []).map(p => p?.id).filter(Boolean), state.genreNationalMapping
  )?.filter(c => c?.category === "LOCAL" || c?.category === "RSN" || !aliases.includes(c?.name));
}

/** ----------------------------------------------------------------------------
 * STATE REDUCER
 * - Handles all state updates for channels in the component
 * - Called as "dispatch({ type: 'ACTION_TYPE', data: { ... } })"
 */
export function chlupStoreReducer(
  state: ChlupState,
  action: ChlupStateActions
): ChlupState {
  if (action.type === 'UPDATE_COMPONENT_VIEW') {
    // [WAT-11911] Neither a label nor a position = the action carries no view intent, so leave
    // the view alone. This used to be free: the old resolver returned undefined for a bare
    // action and the reducer fell through to a silent no-op. The resolver is now TOTAL — it
    // always names a view — so without this line a `{}`-shaped dispatch would throw a user
    // off the categories grid onto packages. Neither live dispatch site can produce that
    // shape (both always send a label); this is the contract for the next one.
    if (!action?.data?.selectedToggle && action?.data?.selectedToggleIndex === undefined) {
      return state;
    }
    const newView = getComponentView(action?.data?.selectedToggle, action?.data?.selectedToggleIndex);
    console.info("[ChlupStore] updating component view to ", newView);

    if (newView === state.activeView) {
      return state; // no-op
    } else if (newView === 'categories') {
      return {
        ...state,
        activeView: newView,
        needsUpdate: {
          ...state.needsUpdate,
          banner: Object.keys(state.bannerChannels)?.length === 0
        }
      }
    } else if (newView === 'packages') {
      // Signature grids must come from the filtered source (NOT raw state.channels), or a
      // signatureExcluded national (e.g. "Telemundo East" on 518) reappears as a "$" add-on
      // row on every toggle back to Signature Packages. [WAT-11301]
      const toggleSignatureGridChannels = getSignatureGridSource(state);
      return !state.cmprGridChannelsAZ?.INCLUDED_CHANNELS || !state.cmprGridChannelsNum?.INCLUDED_CHANNELS || !state.cmprGridChannelsCategorical?.INCLUDED_CHANNELS ?
        {
          ...state,
          activeView: newView,
          cmprGridChannelsAZ: getCmprGridChannelsAZ(
            toggleSignatureGridChannels,
            state?.cache?.gridCategories,
            state?.cache?.gridPackages,
            state?.myFreeKey
          ),
          cmprGridChannelsNum: getCmprGridChannelsNum(
            toggleSignatureGridChannels,
            state?.cache?.gridCategories,
            state?.cache?.gridPackages,
            state?.myFreeKey
          ),
          cmprGridChannelsCategorical: getCmprGridChannelsCategorical(
            toggleSignatureGridChannels,
            state?.cache?.gridCategories,
            state?.cache?.gridPackages,
            state?.myFreeKey
          ),
          needsUpdate: {
            ...state.needsUpdate,
            grid: (Object.keys(state?.cmprGridChannelsAZ)?.length === 0 || Object.keys(state?.cmprGridChannelsNum)?.length === 0 || Object.keys(state?.cmprGridChannelsCategorical)?.length === 0)
          }
        } :
        {
          ...state,
          activeView: newView,
          needsUpdate: {
            ...state.needsUpdate,
            grid: (Object.keys(state?.cmprGridChannelsAZ)?.length === 0 || Object.keys(state?.cmprGridChannelsNum)?.length === 0 || Object.keys(state?.cmprGridChannelsCategorical)?.length === 0)
          }
        }
    }

    // [WAT-11911] Unreachable by contract now: getComponentView always returns one of the two
    // views handled above. It used to be THE prod failure path — the old resolver fell through
    // to undefined and this silent no-op left the page blank. Kept as a backstop, but if you
    // ever widen that return type, handle the new view above rather than letting it land here:
    // a third view falling into this line reopens the exact outage this ticket closed.
    return state;
  } else if (action.type === 'SET_LOADING_LOCAL_RSN_CHANNELS') {
    if (state.activeView === 'categories') {
      return {
        ...state,
        bannerChannels: filterCategoricalChannels(
          {
            local: false,
            rsn: false
          },
          state.bannerChannels
        )
      }
    }
    return {
      ...state,
      cmprGridChannelsAZ: {
        ...state.cmprGridChannelsAZ,
        ["INCLUDED_CHANNELS"]: queryChannels(
          {
            local: false,
            rsn: false
          },
          state.cmprGridChannelsAZ?.INCLUDED_CHANNELS
        )
      },
      cmprGridChannelsNum: {
        ...state.cmprGridChannelsNum,
        ["INCLUDED_CHANNELS"]: queryChannels(
          {
            local: false,
            rsn: false
          },
          state.cmprGridChannelsNum?.INCLUDED_CHANNELS
        )
      },
      cmprGridChannelsCategorical: {
        ...state.cmprGridChannelsCategorical,
        ["LOCAL"]: mockLocalData,
        ["RSN"]: mockRsnData
      }
    }
  } else if (action.type === 'ADD_LOCAL_RSN_CHANNELS') {
    if (action?.data?.local?.length === 0 && action?.data?.rsn?.length === 0) {
      console.warn("[ChlupStore] Adding Local RSN channels :: local and rsn channels have array length 0");
    }
    console.debug("[ChlupStore] adding local/rsn channels...", action?.data?.local, action?.data?.rsn);

    const newChannels = state.channels
      // filter out old LOCAL and RSN channels, filter in new ones
      ?.filter(c => c?.category !== "LOCAL" && c?.category !== "RSN")
      ?.concat(action.data.local, action.data.rsn)
      ?.sort(Channels.sortAlphabetically);

    const newChannelsRemoveMyHomeTeamDuplicates =
      newChannels?.filter(c => {
        if (c?.chnlPackages?.includes("ATT_TV_MYHOMETEAM")) {
          // if there's a matching rsn for the channel, don't add
          if (c?.category !== "RSN" && action?.data?.rsn?.some(rsn => rsn?.name?.toLowerCase() === c?.name?.toLowerCase())) {
            return false;
          }
        }
        return true;
      });
    // Generated by Copilot- Start
    const channelAliases = getNationalAliasesForLocalChannels(action.data.local, action?.data?.genreNationalMapping || []);

    // Two tiers [WAT-11301]:
    //  • visibleChannels — the genre-nationals DMA gate ONLY, feeding the genre banner + search.
    //    signatureExcludedLocalChannels is a SIGNATURE-tier concern and must NOT touch the genre
    //    (MiEspañol) view, so a genre-eligible local (e.g. beam "Telemundo") stays here even when the
    //    DMA is signatureExcluded; the gate renames a national alias to its base and drops the
    //    alias-named beam pseudo-local / out-of-market master copy.
    //  • removedChannelNames (per-package over the displayed signature tiers, plus the aliases) —
    //    stripped from every signature grid bucket, including ADD_ON_CHANNELS, so that national
    //    never renders a ✓ or "$" in the English tier columns.
    const visibleChannels = filterAllChannelsNationalByDma(
      newChannelsRemoveMyHomeTeamDuplicates,
      action.data.genreNationalMapping, action.data.currentDma, Object.keys(state.genreMapping || {})
    );
    const signatureKept = new Set(filterChannelsExcludedFromDisplayedPackages(
      newChannelsRemoveMyHomeTeamDuplicates,
      action.data.currentDma,
      action.data.signatureExcludedLocalChannels,
      (state.cache?.gridPackages || []).map(p => p?.id).filter(Boolean),
      action.data.genreNationalMapping
    ));
    const removedChannelNames = channelAliases.concat(
      (newChannelsRemoveMyHomeTeamDuplicates || []).filter(c => !signatureKept.has(c)).map(c => c?.name).filter(Boolean)
    );
    return {
      ...state,
      channels: newChannelsRemoveMyHomeTeamDuplicates,
      currentDma: action.data.currentDma ?? state.currentDma,
      signatureExcludedLocalChannels: action.data.signatureExcludedLocalChannels ?? state.signatureExcludedLocalChannels,
      cache: {
        ...state.cache,
        localChnls: action.data.local,
        rsnChnls: action.data.rsn
      },
      bannerChannels: getBannerChannels(
        visibleChannels,
        state.cache?.bannerCategories,
        state.cache?.myFreeKey,
        action?.data?.currentDma,
        action?.data?.genreNationalMapping,
        state.genreMapping
      ),
      cmprGridChannelsAZ: {
        ...state.cmprGridChannelsAZ,
        ["INCLUDED_CHANNELS"]: [...(state.initialCmprGridChannelsAZ?.INCLUDED_CHANNELS || [])]
          ?.filter(c => c?.category !== "LOCAL" && c?.category !== "RSN" && !removedChannelNames.includes(c?.name))
          ?.concat(action.data.local?.filter(c => !c?.signatureExcluded) || [], action.data.rsn)
          ?.sort(Channels.sortAlphabetically),
        ["ADD_ON_CHANNELS"]: [...(state.initialCmprGridChannelsAZ?.ADD_ON_CHANNELS || [])]
          ?.filter(c => !removedChannelNames.includes(c?.name))
      },
      cmprGridChannelsNum: {
        ...state.cmprGridChannelsNum,
        ["INCLUDED_CHANNELS"]: [...(state.initialCmprGridChannelsNum?.INCLUDED_CHANNELS || [])]
          ?.filter(c => c?.category !== "LOCAL" && c?.category !== "RSN" && !removedChannelNames.includes(c?.name))
          ?.concat(action.data.local?.filter(c => !c?.signatureExcluded) || [], action.data.rsn)
          ?.sort(Channels.sortByChannelNumber),
        ["ADD_ON_CHANNELS"]: [...(state.initialCmprGridChannelsNum?.ADD_ON_CHANNELS || [])]
          ?.filter(c => !removedChannelNames.includes(c?.name))
      },
      cmprGridChannelsCategorical: {
        ...removeChannelsByAliases(state.initialCmprGridChannelsCategorical, removedChannelNames),
        ["LOCAL"]: action.data?.local?.filter(c => !c?.signatureExcluded)?.sort(Channels.sortAlphabetically),
        ["RSN"]: action.data?.rsn?.sort(Channels.sortAlphabetically)
      },
      searchChoices: getSearchChoices(visibleChannels),
      needsUpdate: {
        banner: false,
        grid: false
      }
    }
    // Generated by Copilot- End
  } else if (action.type === "REFRESH_BANNER") {
    if (!state.needsUpdate.banner) {
      return state;
    } // no-op
    console.info("[ChlupStore] refreshing banner..")
    // Genre-nationals DMA gate ONLY — signatureExcludedLocalChannels never gates the genre view.
    // Same source the ADD_LOCAL_RSN_CHANNELS banner build uses. [WAT-11301]
    const bannerVisibleChannels = filterAllChannelsNationalByDma(
      state?.channels,
      state.genreNationalMapping, state.currentDma, Object.keys(state.genreMapping || {})
    );
    return {
      ...state,
      bannerChannels: getBannerChannels(
        bannerVisibleChannels,
        state.cache?.bannerCategories,
        state.cache?.myFreeKey,
        state.currentDma,
        state.genreNationalMapping,
        state.genreMapping
      ),
      needsUpdate: {
        ...state.needsUpdate,
        banner: false
      }
    }
  } else if (action.type === "REFRESH_PKGS_GRID") {
    if (!state.needsUpdate.banner) {
      return state;
    } // no-op
    console.info("[ChlupStore] refreshing pkgs grid...")
    // Signature grids use the filtered source (per-package exclusion + alias dedup), not the raw master list. [WAT-11301]
    const signatureGridChannels = getSignatureGridSource(state);
    return {
      ...state,
      cmprGridChannelsAZ: getCmprGridChannelsAZ(
        signatureGridChannels,
        state?.cache?.gridCategoriesAZ,
        state?.cache?.gridPackages,
        state?.myFreeKey
      ),
      cmprGridChannelsNum: getCmprGridChannelsNum(
        signatureGridChannels,
        state?.cache?.gridCategories,
        state?.cache?.gridPackages,
        state?.myFreeKey
      ),
      cmprGridChannelsCategorical: getCmprGridChannelsCategorical(
        signatureGridChannels,
        state?.cache?.gridCategories,
        state?.cache?.gridPackages,
        state?.myFreeKey
      ),
      needsUpdate: {
        ...state.needsUpdate,
        grid: false
      }
    }
  }

  // default case
  return state;
}
